<template>
  <div>
    <div class="tw-flex tw-flex-col tw-gap-2">
      <h1 class="tw-font-semibold tw-text-xl lg:tw-text-2xl xl:tw-text-3xl dark--text">{{ $t('user.signInTitle') }}</h1>
      <div class="tw-flex tw-flex-col tw-mt-0 lg:tw-mt-4 tw-gap-1">
        <p class="tw-font-medium tw-opacity-75 xl:tw-text-xl dark--text">{{ $t('user.welcomeToBizoKitchen') }}</p>
        <p class="tw-text-xs tw-opacity-50 lg:tw-text-base dark--text">{{ $t('user.pleaseSignInToYourAccount') }}</p>
      </div>
    </div>
    <div class="lg:tw-mt-10 tw-mt-6">
      <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(onSubmit)">
          <div class="tw-flex tw-flex-col tw-w-full tw-gap-1 tw-text-sm mb-5">
            <label class="grey--text text--darken-3 font-weight-medium mb-1">{{ $t('user.emailAddressOrPhoneNumber') }}</label>
            <ValidationProvider v-slot="{ errors }" name="Username" rules="required">
              <v-text-field
                v-model="phone"
                :error-messages="errors"
                :placeholder="$t('user.enterYourEmailOrPhoneNumber')"
                outlined
                dense
                prepend-inner-icon="mdi-account-outline"
                style="border-radius: 8px; font-size: 14px;"
                hide-details="auto"
                required
              ></v-text-field>
            </ValidationProvider>
          </div>
          <div class="tw-flex tw-flex-col tw-w-full tw-gap-1 tw-text-sm mb-6">
            <label class="grey--text text--darken-3 font-weight-medium mb-1">{{ $t('accounts.password') }}</label>
            <ValidationProvider v-slot="{ errors }" name="Password" rules="required">
              <v-text-field
                v-model="password"
                :error-messages="errors"
                placeholder="••••••••"
                outlined
                dense
                prepend-inner-icon="mdi-lock-outline"
                :append-icon="show1 ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
                :type="show1 ? 'text' : 'password'"
                @click:append="show1 = !show1"
                style="border-radius: 8px; font-size: 14px;"
                hide-details="auto"
                required
              ></v-text-field>
            </ValidationProvider>
          </div>
          <div class="d-flex justify-end mt-6">
            <v-btn
              type="submit"
              :loading="btn_loading"
              color="primary"
              depressed
              height="44"
              style="border-radius: 8px; box-shadow: 0 4px 12px rgba(1, 130, 148, 0.2);"
              class="white--text font-weight-bold px-8 text-capitalize"
            >
              {{ $t('user.signIn2') }}
            </v-btn>
          </div>
        </form>
      </ValidationObserver>
      <div v-if="errorFromApi" class="mt-6 pa-4 rounded-lg d-flex align-center red lighten-5 red--text text--darken-2" style="border: 1px solid rgba(239, 68, 68, 0.15); font-size: 13px; border-radius: 8px;">
        <v-icon color="red darken-2" class="mr-2" size="18">mdi-alert-circle-outline</v-icon>
        <span class="font-weight-medium">{{ errorFromApi }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import i18n from "@/i18n";
import { required, length } from "vee-validate/dist/rules";
import { userSignIn } from "../methods/signin.js";
import { isDietitian } from "../../user/methods/helper.js";
import { checkSubscriptionStatus, isSubscriptionExpired } from "../../utils/subscription-service";
import { loadAppConfigurations } from "../../utils/config-loader";
import { applyEntitlements } from "../../utils/entitlements";

import { extend, ValidationObserver, ValidationProvider, setInteractionMode } from "vee-validate";

setInteractionMode("eager");

// Function messages: vee-validate fills in the field name and rule params, so
// the text must not go through vue-i18n's own {placeholder} substitution as a
// plain string. Resolved per-validation, so they follow the active locale.
extend("required", {
  ...required,
  message: field => i18n.t("validation.cannotBeEmpty", { field }),
});

extend("length", {
  ...length,
  message: (field, values) =>
    i18n.t("validation.phoneDigits", { field, length: values.length }),
});
export default {
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      show1: false,
      phone: "",
      password: "",
      btn_loading: false,
      errorFromApi: null,
      style: {
        borderStyle: { border: "0px solid black !important" },
      },
    };
  },
  methods: {
    onSubmit() {
      this.$refs.observer.validate();
      // this.$store.state.signin = true;

      this.btn_loading = true;
      userSignIn(this.phone, this.password).then(async data => {
        if (data) {
          // console.log(data);
          if (data === true) {
            this.btn_loading = false;
            
            // Load app configurations immediately on login so currency and timezone are synced to localStorage
            try {
              await loadAppConfigurations();
            } catch (configError) {
              console.warn("Failed to load configurations on login:", configError);
            }
            
            // Check subscription immediately after login (token is now set)
            try {
              const subscriptionResult = await checkSubscriptionStatus();
              
              if (subscriptionResult.success && subscriptionResult.data) {
                const expired = isSubscriptionExpired(subscriptionResult.data);
                // plan entitlements first: the router guard and the navbar read them on the way in
                applyEntitlements(subscriptionResult.data);
                
                // Emit event with subscription data
                this.$root.$emit("user-logged-in", {
                  subscriptionData: subscriptionResult.data,
                  expired: expired
                });
                
                // If subscription is expired, still navigate but paywall will show
                if (isDietitian()) {
                  this.$router.replace({ path: "appointments" });
                } else {
                  this.$router.replace({ path: "dashboard" });
                }
              } else {
                // If subscription check fails, proceed with navigation
                this.$root.$emit("user-logged-in");
                if (isDietitian()) {
                  this.$router.replace({ path: "appointments" });
                } else {
                  this.$router.replace({ path: "dashboard" });
                }
              }
            } catch (error) {
              // On error, proceed with navigation
              this.$root.$emit("user-logged-in");
              if (isDietitian()) {
                this.$router.replace({ path: "appointments" });
              } else {
                this.$router.replace({ path: "dashboard" });
              }
            }
          } else {
            this.errorFromApi = this.$t("user.signIn.invalidLogin");
            this.btn_loading = false;
          }
        }
      });
    },
  },
};
</script>

<style>
.inputPhone input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}
.inputPhone input::-webkit-outer-spin-button,
.inputPhone input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
.tw-flex.tw-flex-col.tw-gap-2 p {
  margin-bottom: 0 !important;
}
</style>
